import type { SuggestedChip } from "@/lib/api/chat";

function normalizeChipText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_/\\]/g, " ")
    .replace(/['".,!?;:()[\]{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function chipKey(chip: SuggestedChip): string {
  return `${normalizeChipText(chip.label)}::${normalizeChipText(chip.value)}`;
}

/**
 * Match externally generated chips to an approved chip set.
 *
 * When a backend-generated chip matches an approved label or value, we return
 * the approved chip instead of the raw chip so wording stays canonical.
 */
export function matchSuggestedChipsToApprovedChips(
  chips: readonly SuggestedChip[],
  approvedChips: readonly SuggestedChip[],
): SuggestedChip[] {
  if (chips.length === 0 || approvedChips.length === 0) {
    return [];
  }

  const approvedByLabel = new Map<string, SuggestedChip>();
  const approvedByValue = new Map<string, SuggestedChip>();

  for (const chip of approvedChips) {
    const normalizedLabel = normalizeChipText(chip.label);
    const normalizedValue = normalizeChipText(chip.value);

    if (!approvedByLabel.has(normalizedLabel)) {
      approvedByLabel.set(normalizedLabel, chip);
    }

    if (!approvedByValue.has(normalizedValue)) {
      approvedByValue.set(normalizedValue, chip);
    }
  }

  const matched: SuggestedChip[] = [];
  const seen = new Set<string>();

  for (const chip of chips) {
    const normalizedLabel = normalizeChipText(chip.label);
    const normalizedValue = normalizeChipText(chip.value);
    const approved =
      approvedByLabel.get(normalizedLabel) ??
      approvedByValue.get(normalizedValue);

    if (!approved) {
      continue;
    }

    const key = chipKey(approved);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    matched.push(approved);
  }

  return matched;
}
